import React, { Component } from "react";
import PropTypes from "prop-types";
import { css, StyleSheet } from "aphrodite";

class LazyImage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoaded: false,
      isInView: false,
      hasError: false,
    };
    this.imageRef = React.createRef();
    this.imgEl = React.createRef();
    this.observer = null;
  }

  componentDidMount() {
    // Create intersection observer for lazy loading
    if ("IntersectionObserver" in window) {
      this.observer = new IntersectionObserver(this.handleIntersection, {
        rootMargin: "50px 0px", // Load images 50px before they come into view
        threshold: 0.1,
      });

      if (this.imageRef.current) {
        this.observer.observe(this.imageRef.current);
      }
    } else {
      // Fallback for browsers without IntersectionObserver
      this.setState({ isInView: true });
    }

    // Additional check for immediate load if already in view
    setTimeout(() => {
      if (this.state.isInView && !this.state.isLoaded) {
        this.checkImageLoaded();
      }
    }, 100);
  }

  componentWillUnmount() {
    if (this.observer && this.imageRef.current) {
      this.observer.unobserve(this.imageRef.current);
    }
  }

  handleIntersection = (entries) => {
    const [entry] = entries;
    if (entry.isIntersecting) {
      this.setState({ isInView: true });
      // Stop observing once image is in view
      if (this.observer) {
        this.observer.unobserve(entry.target);
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    // If we just became visible, or src changed, check if image is already cached/complete
    if (
      (this.state.isInView && !prevState.isInView) ||
      (this.props.src !== prevProps.src && this.state.isInView)
    ) {
      // Some browsers don't fire onLoad for cached images
      if (!this.checkImageLoaded()) {
        // Re-check shortly in case the image becomes complete quickly
        setTimeout(this.checkImageLoaded, 50);
      }
    }
  }

  handleImageLoad = () => {
    this.setState({ isLoaded: true });
    if (this.props.onLoad) {
      this.props.onLoad();
    }
  };

  handleImageError = (e) => {
    this.setState({ hasError: true });
    if (this.props.onError) {
      this.props.onError(e);
    }
  };

  // Extra safety: some browsers skip onLoad for cached images or when
  // loading attribute interferes. Ensure we mark as loaded when possible.
  checkImageLoaded = () => {
    const img = this.imgEl && this.imgEl.current;
    if (img && img.complete && img.naturalWidth > 0 && !this.state.isLoaded) {
      this.setState({ isLoaded: true, hasError: false });
      if (this.props.onLoad) this.props.onLoad();
      return true;
    }
    return false;
  };

  render() {
    const {
      src,
      alt,
      title,
      className,
      style,
      imgStyle,
      placeholder,
      placeholderColor = "#f8f9fa",
      fallbackSrc,
      loading = "lazy",
      containerClassName,
      imageClassName,
      fit,
      ...otherProps
    } = this.props;

    const { isLoaded, isInView, hasError } = this.state;

    // Build class names safely:
    // - Aphrodite styles must go through css(...)
    // - Plain className strings should be concatenated, not passed to css(...)
    const containerClassNameSafe = [css(styles.container), containerClassName]
      .filter(Boolean)
      .join(" ");

    const imageExtraClass = [className, imageClassName]
      .filter(Boolean)
      .join(" ");

    // Avoid double-lazy: since we only render <img> when inView is true,
    // instruct browser to load eagerly once mounted for reliability.
    const effectiveLoading = isInView ? "eager" : loading;

    // Prepare img inline style to override defaults when needed
    const baseImgStyle = {};
    if (fit) {
      baseImgStyle.objectFit = fit; // override default cover if provided
    }
    const finalImgStyle = Object.assign({}, baseImgStyle, imgStyle || {});

    return (
      <div ref={this.imageRef} className={containerClassNameSafe} style={style}>
        {/* Placeholder shown while image loads */}
        {!isLoaded && !hasError && (
          <div
            className={css(styles.placeholder)}
            style={{ backgroundColor: placeholderColor }}
          >
            {placeholder || (
              <div className={css(styles.placeholderIcon)}>
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 4H20C20.5523 4 21 4.44772 21 5V19C21 19.5523 20.5523 20 20 20H4C3.44772 20 3 19.5523 3 19V5C3 4.44772 3.44772 4 4 4Z"
                    stroke="#ccc"
                    strokeWidth="2"
                  />
                  <path
                    d="M9 9C9.55228 9 10 8.55228 10 8C10 7.44772 9.55228 7 9 7C8.44772 7 8 7.44772 8 8C8 8.55228 8.44772 9 9 9Z"
                    fill="#ccc"
                  />
                  <path
                    d="M21 15L16 10L5 21"
                    stroke="#ccc"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            )}
          </div>
        )}

        {/* Actual image - only loaded when in view */}
        {isInView && !hasError && (
          <img
            ref={this.imgEl}
            src={src}
            alt={alt}
            title={title}
            loading={effectiveLoading}
            className={[
              css(styles.image, isLoaded && styles.imageLoaded),
              imageExtraClass,
            ]
              .filter(Boolean)
              .join(" ")}
            style={finalImgStyle}
            onLoad={this.handleImageLoad}
            onError={this.handleImageError}
            onAbort={this.handleImageError}
            {...otherProps}
          />
        )}

        {/* Fallback image or error state */}
        {hasError && fallbackSrc && (
          <img
            ref={this.imgEl}
            src={fallbackSrc}
            alt={alt}
            title={title}
            loading={effectiveLoading}
            className={[css(styles.image, styles.imageLoaded), imageExtraClass]
              .filter(Boolean)
              .join(" ")}
            style={finalImgStyle}
            {...otherProps}
          />
        )}
      </div>
    );
  }
}

LazyImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  title: PropTypes.string,
  // DEPRECATED: Use imageClassName for image, containerClassName for wrapper
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  imageClassName: PropTypes.string,
  style: PropTypes.object,
  placeholder: PropTypes.node,
  placeholderColor: PropTypes.string,
  fallbackSrc: PropTypes.string,
  loading: PropTypes.oneOf(["lazy", "eager"]),
  imgStyle: PropTypes.object,
  fit: PropTypes.oneOf(["cover", "contain", "fill", "none", "scale-down"]),
  onLoad: PropTypes.func,
  onError: PropTypes.func,
};

const styles = StyleSheet.create({
  container: {
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  placeholder: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8f9fa",
    animation: "pulse 1.5s ease-in-out infinite",
  },
  placeholderIcon: {
    opacity: 0.5,
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  imageLoaded: {
    opacity: 1,
  },
  "@keyframes pulse": {
    "0%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.7,
    },
    "100%": {
      opacity: 1,
    },
  },
});

export default LazyImage;



// WEBPACK FOOTER //
// ./src/components/shared/LazyImage.js